import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getCampaignsApi, getBrandsApi, getSlotsApi, getCohortsApi } from '../../utils/api';
import { formatNumber, formatRupees } from '../../utils/helpers';
import MetricCard from '../../components/MetricCard';
import { TrendingUp, Users, Eye, MousePointer, DollarSign } from 'lucide-react';

const MyMetrics = ({ showToast, selectedBrand, isViewOnly }) => {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [campaigns, setCampaigns] = useState([]);
  const [brand, setBrand] = useState(null);
  const [slots, setSlots] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [user, selectedBrand]);

  const loadData = async () => {
    try {
      // Viewers don't have a fixed brand_id — use selectedBrand instead
      const activeBrandId = isViewer ? selectedBrand : user.brand_id;
      
      const [campaignsRes, brandsRes, slotsRes, cohortsRes] = await Promise.all([
        getCampaignsApi(activeBrandId),
        getBrandsApi(),
        getSlotsApi(),
        getCohortsApi()
      ]);
      
      setCampaigns(campaignsRes.data);
      const userBrand = brandsRes.data.find(b => b.id === activeBrandId);
      setBrand(userBrand);
      setSlots(slotsRes.data);
      setCohorts(cohortsRes.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load metrics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const aggregateMetrics = () => {
    return campaigns.reduce(
      (acc, campaign) => {
        acc.sent += campaign.metrics?.sent || 0;
        acc.delivered += campaign.metrics?.delivered || 0;
        acc.seen += campaign.metrics?.seen || 0;
        acc.clicks += campaign.metrics?.clicks || 0;
        acc.revenue += campaign.metrics?.revenue || 0;
        return acc;
      },
      { sent: 0, delivered: 0, seen: 0, clicks: 0, revenue: 0 }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const aggregate = aggregateMetrics();
  const deliveryRate = aggregate.sent > 0 ? ((aggregate.delivered / aggregate.sent) * 100).toFixed(1) : 0;
  const seenRate = aggregate.delivered > 0 ? ((aggregate.seen / aggregate.delivered) * 100).toFixed(1) : 0;
  const ctr = aggregate.seen > 0 ? ((aggregate.clicks / aggregate.seen) * 100).toFixed(1) : 0;
  const totalSpends = (aggregate.delivered * 0.98).toFixed(2);
  const totalRoas = parseFloat(totalSpends) > 0 ? (aggregate.revenue / parseFloat(totalSpends)).toFixed(2) : 0;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="relative inline-block">
          <h1 className="text-3xl font-bold text-white mb-2">Campaign Performance</h1>
          <span className="absolute -top-1 -right-14 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
            BETA
          </span>
        </div>
        <p className="text-slate-400">Metrics for your brand's WhatsApp campaigns</p>
      </div>

      {/* Aggregate Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="text-blue-400" size={20} />
            <span className="text-sm text-slate-400">Sent</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(aggregate.sent)}</p>
        </div>
        
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="text-green-400" size={20} />
            <span className="text-sm text-slate-400">Delivered</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(aggregate.delivered)}</p>
          <p className="text-xs text-slate-500 mt-1">{deliveryRate}% rate</p>
        </div>
        
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="text-purple-400" size={20} />
            <span className="text-sm text-slate-400">Seen</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(aggregate.seen)}</p>
          <p className="text-xs text-slate-500 mt-1">{seenRate}% rate</p>
        </div>
        
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <MousePointer className="text-yellow-400" size={20} />
            <span className="text-sm text-slate-400">Clicks</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(aggregate.clicks)}</p>
          <p className="text-xs text-slate-500 mt-1">{ctr}% CTR</p>
        </div>
        
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="text-green-400" size={20} />
            <span className="text-sm text-slate-400">Revenue</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatRupees(aggregate.revenue)}</p>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="space-y-4">
        {campaigns.length === 0 ? (
          <div className="bg-slate-800 rounded-lg p-12 text-center">
            <p className="text-slate-400">No campaigns yet</p>
          </div>
        ) : (
          campaigns.map((campaign) => {
            const slot = slots.find(s => s.id === campaign.slot_id);
            const cohort = cohorts.find(c => c.id === campaign.cohort_id);

            return (
              <MetricCard
                key={campaign.id}
                campaign={campaign}
                brand={brand}
                slot={slot}
                cohort={cohort}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

export default MyMetrics;
